import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../config";
import "../css/InstClassForm.css";
import { FaArrowLeft, FaSchool, FaChevronRight } from "react-icons/fa";
import { MdDashboard, MdClass } from "react-icons/md";
import { HiSparkles } from "react-icons/hi2";
import {
  Skeleton,
  Box,
  Stack,
  Typography,
  IconButton,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";

/* ─── Design Tokens ─────────────────────────────────────────── */
const T = {
  blue: "#1565C0",
  blueMid: "#1E88E5",
  blueGhost: "rgba(21,101,192,0.07)",
  blueGhostHover: "rgba(21,101,192,0.13)",
  slate900: "#0D1B2A",
  slate700: "#334155",
  slate500: "#64748B",
  slate400: "#94A3B8",
  slate300: "#CBD5E1",
  slate200: "#E2E8F0",
  slate100: "#F1F5F9",
  slate50: "#F8FAFC",
  white: "#FFFFFF",
  amber: "#F57C00",
  teal: "#00796B",
  grad: "linear-gradient(135deg, #1565C0 0%, #1E88E5 100%)",
  gradSoft:
    "linear-gradient(135deg, rgba(21,101,192,0.06) 0%, rgba(30,136,229,0.02) 100%)",
  shadow: "0 1px 3px rgba(13,27,42,0.06), 0 4px 16px rgba(13,27,42,0.04)",
  shadowHover: "0 6px 24px rgba(21,101,192,0.14)",
};

/* ─── Color palette for class number badges ─────────────────── */
const BADGE_PALETTES = [
  ["#1565C0", "#42A5F5"],
  ["#3949AB", "#7986CB"],
  ["#00796B", "#4DB6AC"],
  ["#AD1457", "#F48FB1"],
  ["#6A1B9A", "#CE93D8"],
  ["#F57C00", "#FFB74D"],
  ["#558B2F", "#AED581"],
  ["#0277BD", "#80DEEA"],
];

/* ─── Motion Wrapper ─────────────────────────────────────────── */
const FadeIn = ({ children, delay = 0, y = 10 }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1], delay }}
  >
    {children}
  </motion.div>
);

/* ─── Skeleton Class Row ─────────────────────────────────────── */
const SkeletonRow = ({ delay }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.3, delay }}
  >
    <Box
      sx={{
        p: 2,
        borderRadius: 2.5,
        border: `1px solid ${T.slate200}`,
        background: T.white,
        display: "flex",
        alignItems: "center",
        gap: 2,
      }}
    >
      <Skeleton variant="circular" width={44} height={44} />
      <Box sx={{ flex: 1 }}>
        <Skeleton width="40%" height={16} />
        <Skeleton width="22%" height={12} sx={{ mt: 0.5 }} />
      </Box>
      <Skeleton width={28} height={28} sx={{ borderRadius: 1.5 }} />
    </Box>
  </motion.div>
);

/* ─── Class Card ─────────────────────────────────────────────── */
const ClassCard = ({ cls, index, onClick }) => {
  const [c1, c2] = BADGE_PALETTES[index % BADGE_PALETTES.length];
  const initial = cls.name?.charAt(0).toUpperCase() || "C";

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.35,
        delay: index * 0.055,
        ease: [0.22, 1, 0.36, 1],
      }}
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.985 }}
    >
      <Box
        onClick={onClick}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: { xs: 1.8, sm: 2.2 },
          borderRadius: 2.5,
          cursor: "pointer",
          background: T.white,
          border: `1px solid ${T.slate200}`,
          boxShadow: T.shadow,
          transition: "all 0.22s ease",
          "&:hover": {
            borderColor: `${T.blue}40`,
            boxShadow: T.shadowHover,
            background: T.gradSoft,
          },
          "&:hover .chevron-icon": {
            opacity: 1,
            transform: "translateX(3px)",
            color: T.blue,
          },
          "&:hover .class-number": {
            boxShadow: `0 4px 14px ${c1}45`,
          },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          {/* Badge */}
          <Box
            className="class-number"
            sx={{
              width: { xs: 40, sm: 46 },
              height: { xs: 40, sm: 46 },
              borderRadius: 2,
              flexShrink: 0,
              background: `linear-gradient(135deg, ${c1}, ${c2})`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: `0 3px 10px ${c1}38`,
              transition: "box-shadow 0.25s ease",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Shine overlay */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background:
                  "linear-gradient(135deg, rgba(255,255,255,0.22) 0%, transparent 60%)",
                pointerEvents: "none",
              }}
            />
            <Typography
              sx={{
                fontWeight: 900,
                color: "#fff",
                fontSize: { xs: "0.78rem", sm: "0.88rem" },
                lineHeight: 1,
                letterSpacing: "-0.3px",
                position: "relative",
                zIndex: 1,
              }}
            >
              {initial}
            </Typography>
          </Box>

          {/* Text */}
          <Box>
            <Typography
              sx={{
                fontWeight: 800,
                color: T.slate900,
                fontSize: { xs: "0.88rem", sm: "0.95rem" },
                textTransform: "capitalize",
                lineHeight: 1.25,
                letterSpacing: "-0.2px",
              }}
            >
              {cls.name}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={0.8} mt={0.4}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: cls.is_approved ? T.teal : T.slate300,
                  boxShadow: cls.is_approved
                    ? `0 0 0 2px rgba(0,121,107,0.2)`
                    : "none",
                }}
              />
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  color: T.slate400,
                  fontWeight: 600,
                }}
              >
                {cls.is_approved ? "Approved" : "Pending"} · Tap to manage
              </Typography>
            </Stack>
          </Box>
        </Stack>

        {/* Chevron */}
        <Box
          className="chevron-icon"
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: T.blueGhost,
            border: `1px solid rgba(21,101,192,0.15)`,
            color: T.slate400,
            flexShrink: 0,
            opacity: 0.6,
            transition: "all 0.22s ease",
          }}
        >
          <FaChevronRight size={12} />
        </Box>
      </Box>
    </motion.div>
  );
};

/* ─── Empty State ────────────────────────────────────────────── */
const EmptyState = () => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
  >
    <Box
      sx={{
        textAlign: "center",
        py: { xs: 7, sm: 10 },
        px: 3,
        border: `1.5px dashed rgba(21,101,192,0.18)`,
        borderRadius: 3,
        background:
          "linear-gradient(135deg, rgba(21,101,192,0.025), rgba(255,255,255,0))",
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: 3.5,
          mx: "auto",
          mb: 2.5,
          background: T.blueGhost,
          border: `1px dashed rgba(21,101,192,0.25)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <FaSchool size={26} color={T.slate400} />
      </Box>
      <Typography
        sx={{
          color: T.slate700,
          fontWeight: 800,
          fontSize: "0.95rem",
          mb: 0.5,
        }}
      >
        No classes found
      </Typography>
      <Typography sx={{ color: T.slate400, fontSize: "0.8rem" }}>
        Go back to the dashboard and create your first class.
      </Typography>
    </Box>
  </motion.div>
);

/* ─── Main Component ─────────────────────────────────────────── */
const InstClassList = () => {
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchClasses = async () => {
      const tokenString = localStorage.getItem("authToken");
      const token = tokenString ? JSON.parse(tokenString) : null;
      try {
        setLoading(true);
        const res = await fetch(`${API_URL}/instclass/`, {
          headers: { Authorization: `Bearer ${token.access}` },
        });
        if (res.ok) {
          const data = await res.json();
          setClasses(Array.isArray(data) ? data : data.classes || []);
        } else {
          console.error(`Error fetching classes: ${res.status}`);
        }
      } catch (error) {
        console.error("Fetch error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchClasses();
  }, [navigate]);

  const navigateToSub = (cls) => {
    navigate(`/instsub/${cls.id}`, { state: { classData: cls } });
  };

  /* ─ RENDER ─────────────────────────────────────────────────── */
  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `radial-gradient(ellipse 100% 45% at 70% -5%, rgba(21,101,192,0.07) 0%, transparent 55%), ${T.slate50}`,
        fontFamily: "'DM Sans', 'Nunito', system-ui, sans-serif",
      }}
    >
      {/* ── Sticky Top Nav ──────────────────────────────────────── */}
      <FadeIn>
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 100,
            background: "rgba(255,255,255,0.92)",
            backdropFilter: "blur(16px)",
            borderBottom: `1px solid ${T.slate200}`,
            px: { xs: 2, sm: 3, md: 5 },
            py: 1.5,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{
                width: 34,
                height: 34,
                borderRadius: 1.8,
                color: T.blue,
                background: T.blueGhost,
                border: `1px solid rgba(21,101,192,0.22)`,
                "&:hover": { background: T.blueGhostHover },
              }}
            >
              <FaArrowLeft size={13} />
            </IconButton>
        
            <Stack direction="row" alignItems="center" spacing={0.8}>
              <MdDashboard size={15} color={T.blue} />
              <Typography
                sx={{ fontSize: "0.78rem", color: T.slate400, fontWeight: 600 }}
              >
                Dashboard
              </Typography>
              <Typography sx={{ fontSize: "0.78rem", color: T.slate300 }}>
                /
              </Typography>
              <Typography
                sx={{ fontSize: "0.78rem", color: T.slate700, fontWeight: 700 }}
              >
                Classes
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </FadeIn>

      <Box
        sx={{
          px: { xs: 2, sm: 3, md: 5 },
          py: { xs: 3, md: 4.5 },
          maxWidth: 860,
          mx: "auto",
        }}
      >
        {/* ── Page Heading ─────────────────────────────────────── */}
        <FadeIn delay={0.05}>
          <Box sx={{ mb: 4 }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={0.6}>
              <HiSparkles size={16} color={T.amber} />
              <Typography
                sx={{
                  fontSize: "0.65rem",
                  fontWeight: 800,
                  color: T.amber,
                  textTransform: "uppercase",
                  letterSpacing: "1.2px",
                }}
              >
                Class Management
              </Typography>
            </Stack>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 900,
                color: T.slate900,
                letterSpacing: "-0.8px",
                lineHeight: 1.1,
                fontSize: { xs: "1.6rem", sm: "2rem" },
              }}
            >
              Your Classes
            </Typography>
            <Typography
              sx={{ color: T.slate500, mt: 0.6, fontSize: "0.88rem" }}
            >
              {loading
                ? "Loading your classes…"
                : `${classes.length} class${
                    classes.length !== 1 ? "es" : ""
                  } available — select one to manage subjects & students.`}
            </Typography>
          </Box>
        </FadeIn>

        {/* ── Main Card ────────────────────────────────────────── */}
        <FadeIn delay={0.1}>
          <Box
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              border: `1px solid ${T.slate200}`,
              boxShadow: T.shadow,
              background: T.white,
            }}
          >
            {/* Card Header */}
            <Box
              sx={{
                px: { xs: 2.5, sm: 3 },
                py: 2.2,
                borderBottom: `1px solid ${T.slate100}`,
                background: T.gradSoft,
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                      background: T.grad,
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 4px 12px rgba(21,101,192,0.3)",
                    }}
                  >
                    <MdClass size={16} color="#fff" />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        color: T.slate900,
                        fontSize: "0.92rem",
                      }}
                    >
                      Class Directory
                    </Typography>
                    <Typography sx={{ fontSize: "0.7rem", color: T.slate400 }}>
                      Select a class to manage subjects & students
                    </Typography>
                  </Box>
                </Stack>

                {/* Count badge */}
                {!loading && classes.length > 0 && (
                  <Box
                    sx={{
                      px: 1.8,
                      py: 0.4,
                      borderRadius: 99,
                      background: T.blueGhost,
                      border: `1px solid rgba(21,101,192,0.2)`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.72rem",
                        fontWeight: 800,
                        color: T.blue,
                      }}
                    >
                      {classes.length}{" "}
                      {classes.length === 1 ? "class" : "classes"}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>

            {/* Card Body */}
            <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
              <AnimatePresence mode="wait">
                {loading ? (
                  <Stack spacing={1.5} key="loading">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <SkeletonRow key={i} delay={i * 0.06} />
                    ))}
                  </Stack>
                ) : classes.length === 0 ? (
                  <EmptyState key="empty" />
                ) : (
                  <Stack spacing={1.4} key="list">
                    {classes
                      .slice()
                      .sort((a, b) =>
                        (a.name || "").localeCompare(b.name || "", undefined, {
                          sensitivity: "base",
                        })
                      )
                      .map((cls, index) => (
                        <ClassCard
                          key={cls.id}
                          cls={cls}
                          index={index}
                          onClick={() => navigateToSub(cls)}
                        />
                      ))}
                  </Stack>
                )}
              </AnimatePresence>
            </Box>

            {/* Card Footer — only when list is populated */}
            {!loading && classes.length > 0 && (
              <Box
                sx={{
                  px: 3,
                  py: 1.5,
                  borderTop: `1px solid ${T.slate100}`,
                  background: T.slate50,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.68rem",
                    color: T.slate400,
                    fontWeight: 500,
                  }}
                >
                  Showing all {classes.length} class
                  {classes.length !== 1 ? "es" : ""} · Click a class to open it
                </Typography>
              </Box>
            )}
          </Box>
        </FadeIn>
      </Box>
    </Box>
  );
};

export default InstClassList;
